"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import FilmPauzeKnop from "@/components/film/FilmPauzeKnop";
import {
  abonneerFilmStand,
  bewegingIsUit,
  dealFilmUit,
  dealFilmUitgesloten,
  meldDealFilm,
  useDealFilms,
  zetBewegingAan,
  zetBewegingUit,
} from "@/lib/dealfilm";
import { getStorage, kiesVariant, magFilmInBrowser } from "@/lib/film-voorwaarden";

// Herofilm over de hoofdfoto bovenaan de dealpagina van gewone deals (Dennis
// 26-10-2026), zoals HeroFilm op de homepage: server-render leeg, de foto blijft
// het LCP-element, de film komt uit het register (GET /deal-video) na `load` +
// idle. `hero` vanaf 768 px breed, anders `band`. Gedempt, in een lus; bij een
// fout of hapering terug naar de foto. De enige bediening is de ronde pauzeknop
// (FilmPauzeKnop), die in `stopSlot` wordt gezet zodat hij niet in de foto-knop
// van de galerij komt te staan. Pauze laat de film op het huidige beeld staan en
// zet de sitevoorkeur; komt de bezoeker terug met die voorkeur, dan staat de foto
// er met de knop in play-stand.

const STALL_MS = 3000;

// Bewegingsvoorkeur bij elke montage en bij wijzigingen (reduced-motion,
// "Beweging uit"), want de gedeelde filmmap wordt maar één keer gecontroleerd.
function abonneerBeweging(cb) {
  let mq = null;
  try {
    mq = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;
  } catch {
    mq = null;
  }
  if (mq) {
    if (mq.addEventListener) mq.addEventListener("change", cb);
    else if (mq.addListener) mq.addListener(cb);
  }
  window.addEventListener("storage", cb);
  const afFilmStand = abonneerFilmStand(cb);
  return () => {
    if (mq) {
      if (mq.removeEventListener) mq.removeEventListener("change", cb);
      else if (mq.removeListener) mq.removeListener(cb);
    }
    window.removeEventListener("storage", cb);
    afFilmStand();
  };
}

export default function DealHeroFilm({ dealsId, deal = null, stopSlot = null, plek = "hero" }) {
  const { locale, domain } = useSiteContext();
  // ING en extern verkochte deals krijgen nooit een film (ook niet als de pagina
  // het dealsId toch doorgeeft).
  const uitgesloten = dealFilmUitgesloten(deal, { locale });
  // ookUit: ook met de voorkeur gezet het register lezen, voor de play-knop.
  const dealFilms = useDealFilms({ actief: !uitgesloten, ookUit: true });
  // Omgeving (reduced-motion, Save-Data, 2g) zonder de sitevoorkeur; die staat apart.
  // Eén abonnement met een primitieve stand ("mag|voorkeur uit"), zodat elke melding
  // (reduced-motion, storage, pauzeknop) beide waarden opnieuw leest.
  const beweging = useSyncExternalStore(
    abonneerBeweging,
    () => `${magFilmInBrowser({ domain, storage: getStorage(), uitKey: null })}|${bewegingIsUit()}`,
    () => "true|false",
  );
  const magBewegen = beweging.startsWith("true");
  const voorkeurUit = beweging.endsWith("true");
  const [weg, setWeg] = useState(false);
  const [playing, setPlaying] = useState(false);
  // De bezoeker pauzeerde deze film en hij blijft op het huidige beeld staan.
  const [pauze, setPauze] = useState(false);
  // De film blijft bewaard, ook als de gedeelde map door "pauze" wordt geleegd.
  const [bewaard, setBewaard] = useState(null);
  const videoRef = useRef(null);
  const stallRef = useRef(null);
  const zichtbaarRef = useRef(true);
  const gemeldRef = useRef(false);
  const pauzeRef = useRef(false);
  // Play vanuit de foto-met-knop-stand: de video bestaat nog niet, speel zodra hij gemonteerd is.
  const speelNaMontageRef = useRef(false);

  const uitRegister = uitgesloten ? null : dealFilmUit(dealFilms, dealsId);
  if (uitRegister && uitRegister !== bewaard) setBewaard(uitRegister);
  const registerFilm = uitgesloten ? null : uitRegister || bewaard;
  // Dezelfde keuze om te spelen en om de play-knop te tonen: hero (breed) of band.
  let speelbaar = null;
  if (registerFilm) {
    const variant = kiesVariant(typeof window !== "undefined" && window.matchMedia ? window.matchMedia.bind(window) : null);
    speelbaar = (variant === "desktop" ? registerFilm.hero : registerFilm.band) || null;
  }
  // Voorkeur uit en niet door deze bezoeker net gepauzeerd: foto met knop in play-stand.
  const alleenKnop = voorkeurUit && !pauze;
  const src = speelbaar && !weg && magBewegen && !alleenKnop ? speelbaar : null;
  const meet = { dealsId, plek, groep: dealFilms.groep };

  function meld(soort) {
    if (gemeldRef.current) return;
    gemeldRef.current = true;
    meldDealFilm(soort, meet);
  }

  function verwijder(soort) {
    clearTimeout(stallRef.current);
    setPlaying(false);
    setWeg(true);
    if (soort) meld(soort);
  }

  // Pauzeren buiten beeld of in een verborgen tabblad.
  useEffect(() => {
    if (!src) return undefined;
    const speel = () => {
      const v = videoRef.current;
      if (!v || pauzeRef.current || !zichtbaarRef.current || document.visibilityState === "hidden") return;
      try {
        const p = v.play();
        if (p && p.catch) p.catch(() => {});
      } catch {
        // autoplay geweigerd: foto blijft
      }
    };
    const pauzeer = () => {
      try {
        videoRef.current && videoRef.current.pause();
      } catch {
        // niets te pauzeren
      }
    };
    const onVis = () => (document.visibilityState === "hidden" ? pauzeer() : speel());
    document.addEventListener("visibilitychange", onVis);
    if (speelNaMontageRef.current) {
      speelNaMontageRef.current = false;
      speel();
    }
    let io = null;
    const video = videoRef.current;
    if (video && typeof IntersectionObserver === "function") {
      io = new IntersectionObserver((entries) => {
        const last = entries[entries.length - 1];
        zichtbaarRef.current = !!(last && last.isIntersecting);
        if (zichtbaarRef.current) speel();
        else pauzeer();
      });
      io.observe(video.parentElement || video);
    }
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (io) io.disconnect();
      clearTimeout(stallRef.current);
    };
  }, [src]);

  function wissel(e) {
    // De knop staat via een portal in de foto-knop van de galerij: de klik mag
    // niet door de React-boom naar de lightbox bubbelen.
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!pauze && !voorkeurUit) {
      // Pauze: de film blijft op het huidige beeld staan, de voorkeur geldt voor alle films.
      pauzeRef.current = true;
      clearTimeout(stallRef.current);
      try {
        videoRef.current && videoRef.current.pause();
      } catch {
        // niets te pauzeren
      }
      zetBewegingUit();
      setPauze(true);
      meldDealFilm("pauze", meet);
      return;
    }
    // Play: voorkeur weg, register zo nodig opnieuw lezen, film speelt weer.
    pauzeRef.current = false;
    zetBewegingAan();
    setPauze(false);
    const v = videoRef.current;
    if (!v) speelNaMontageRef.current = true;
    else {
      setPlaying(true);
      try {
        const p = v.play();
        if (p && p.catch) p.catch(() => setPlaying(false));
      } catch {
        setPlaying(false);
      }
    }
    meldDealFilm("afspelen", meet);
  }

  if (!speelbaar || weg || !magBewegen) return null;

  const gepauzeerd = alleenKnop || pauze;
  const knop =
    playing || gepauzeerd ? (
      <FilmPauzeKnop
        gepauzeerd={gepauzeerd}
        onWissel={wissel}
        className="pointer-events-auto absolute top-3 right-3"
      />
    ) : null;
  const knopInSlot = knop && stopSlot ? createPortal(knop, stopSlot) : null;
  if (!src) return knopInSlot;

  return (
    <>
      <video
        ref={videoRef}
        key={src}
        src={src}
        poster={registerFilm?.poster || undefined}
        muted
        playsInline
        loop
        autoPlay
        preload="none"
        disablePictureInPicture
        aria-hidden="true"
        data-testid="dealfilm-hero"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ opacity: playing || pauze ? 1 : 0, transition: "opacity 600ms ease" }}
        onPlaying={() => {
          clearTimeout(stallRef.current);
          setPlaying(true);
          meld("getoond");
        }}
        onPause={() => setPlaying(false)}
        onWaiting={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onEmptied={() => setPlaying(false)}
        onError={() => verwijder("fout")}
        onStalled={() => {
          setPlaying(false);
          clearTimeout(stallRef.current);
          // Een gepauzeerde film verdwijnt nooit door een hapering.
          if (pauzeRef.current) return;
          stallRef.current = setTimeout(() => verwijder("fout"), STALL_MS);
        }}
        onTimeUpdate={() => clearTimeout(stallRef.current)}
      />
      {knopInSlot}
    </>
  );
}
